import { inject, Injectable } from '@angular/core';

import { AnalyticsSegmentClient } from './analytics-segment-client';
import { throwNoContextError, throwReservedContextPropertyError } from './analytics.errors';
import {
  AnalyticsConfig,
  AnalyticsProps,
  ANALYTICS_VIEW_CONTEXT,
  SegmentAPIMethods,
  SegmentClientOptions,
} from './analytics.types';

@Injectable()
export abstract class AnalyticsBase implements SegmentAPIMethods {
  public abstract readonly namespace: string;
  public abstract readonly requireViewContext: boolean;
  public defaultTrackOptions: SegmentClientOptions = {
    integrations: {
      All: true,
      Intercom: false,
    },
  };

  private segmentClient = inject(AnalyticsSegmentClient);
  private config = inject(AnalyticsConfig);
  private viewContext = inject(ANALYTICS_VIEW_CONTEXT, { optional: true });

  public track(
    evtName: string,
    evtProps?: AnalyticsProps,
    options: SegmentClientOptions = this.defaultTrackOptions,
  ) {
    const props = this.requireViewContext ? this.withContext(evtProps) : evtProps;

    if (this.requireViewContext && props === undefined) {
      return;
    }

    if (this.namespace !== '') {
      this.segmentClient.track(`[${this.namespace}] ${evtName}`, props, options);
    } else {
      this.segmentClient.track(evtName, props, options);
    }
  }

  public alias(id: number): void {
    this.segmentClient.alias(id);
  }

  public identify(
    userId?: number,
    traits?: Record<string, string | number>,
    options?: SegmentClientOptions,
  ): void {
    this.segmentClient.identify(userId, traits, options);
  }

  public page(
    category: string,
    name: string,
    properties?: any,
    options?: SegmentClientOptions,
  ): void {
    this.segmentClient.page(category, name, properties, options);
  }

  public reset() {
    this.segmentClient.reset();
  }

  private withContext(evtProps: AnalyticsProps = {}) {
    if (!Array.isArray(this.viewContext) || this.viewContext.length === 0) {
      if (this.config?.isDebug) {
        throwNoContextError();
      } else {
        return;
      }
    }

    if (evtProps && evtProps.viewContext) {
      if (this.config?.isDebug) {
        throwReservedContextPropertyError();
      } else {
        return;
      }
    }

    return {
      ...evtProps,
      viewContext: this.viewContext,
    };
  }
}
